import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import SearchPage from './Pages/SearchPage/SearchPage';
import { EstimatorDashboardPropTypes } from 'src/interfaces/iproptypes';
import ReportPage from './Pages/ReportPage/ReportPage';
import { callApi } from 'src/core/callApi';
import { FullScreenAnimationLoader } from 'src/App/Dashboards/Market/Components/Loaders';
import AutohideSnackbar from 'src/App/Dashboards/Market/Components/Alerts/AutohideSnackbar';
import { convertApiCurrency } from './helper';
import { deepCopyObject, getUserPreferredCurrency } from 'src/utils/functions';
import { User } from 'src/interfaces/iuser';
import { useDispatch } from 'react-redux';
import { deductEstimatorCreditAction } from 'src/store/Dashboard/actions';
import { geocodeAddress } from 'src/utils/functions';
import ComingSoonModal from 'src/App/Dashboards/Market/Modals/ComingSoonModal';
import { useSetUserPaymentInterest } from 'src/hooks/useCustomHooks';
import { CALCULATOR_API_URL } from 'src/utils/const';
import { KpiPercentileType } from './propTypes';
import RateLimitAlert from 'src/App/Dashboards/Market/Components/Alerts/RateLimitAlert';
import { Box, CircularProgress, Grid } from '@mui/material';

const getCreateReportBodyFromHistoryState = async estimatorHistoryState => {
  const address_full = estimatorHistoryState.address_full;

  let latitude: number | null = estimatorHistoryState.latitude
    ? parseFloat(estimatorHistoryState.latitude)
    : null;
  let longitude: number | null = estimatorHistoryState.longitude
    ? parseFloat(estimatorHistoryState.longitude)
    : null;

  if (!latitude || !longitude) {
    const coord = await geocodeAddress(address_full);
    if (coord && Array.isArray(coord) && coord[0] !== 0 && coord[1] !== 0) {
      latitude = coord[0];
      longitude = coord[1];
    }
  }

  const address_short = estimatorHistoryState.address_short
    ? estimatorHistoryState.address_short
    : '';
  const bedrooms = estimatorHistoryState.bedrooms ? parseFloat(estimatorHistoryState.bedrooms) : 2;
  const report_type = estimatorHistoryState.report_type
    ? estimatorHistoryState.report_type
    : 'manage';
  const property_price = estimatorHistoryState.property_price
    ? parseFloat(estimatorHistoryState.property_price)
    : null;
  const monthly_rent = estimatorHistoryState.monthly_rent
    ? parseFloat(estimatorHistoryState.monthly_rent)
    : null;
  let currentReportType = report_type;

  const createRequestObj: { [key: string]: string | number | null } = {
    address_full,
    address_short,
    latitude,
    longitude,
    bedrooms,
    report_type,
    property_price,
    monthly_rent,
  };

  // update local storage with landing report_type
  if (report_type && report_type !== '') {
    localStorage.setItem('landing_report_type', report_type);
  }

  if (
    report_type === 'buy' &&
    (!createRequestObj.property_price || createRequestObj.property_price == 0)
  ) {
    currentReportType = 'manage';
  }

  if (
    report_type === 'rent' &&
    (!createRequestObj.monthly_rent || createRequestObj.monthly_rent == 0)
  ) {
    currentReportType = 'manage';
  }

  if (currentReportType === 'manage') {
    delete createRequestObj.property_price;
    delete createRequestObj.monthly_rent;
    createRequestObj.report_type = 'manage';
  }

  if (currentReportType === 'buy') {
    delete createRequestObj.monthly_rent;
  }

  if (currentReportType === 'rent') {
    delete createRequestObj.property_price;
  }
  return createRequestObj;
};

type EstimatorSavedReportsState = {
  data: any[] | null;
  tag_summary: { name: string; count: number }[];
  untagged_count: number;
  loading: boolean;
};

const handleCreditDeduction = (user: User, dispatch) => {
  const isUserHavingCredits: boolean = user.estimatorCredits > 0;
  const isUserSubscribed: boolean = user.subscriptionPlan != 'starter';

  if (isUserHavingCredits && !isUserSubscribed) {
    dispatch(deductEstimatorCreditAction(1));
  }
};

const CALCULATOR_PATH_PREFIX = 'airbnb-calculator';

/**
 * Build a pathname that matches React Router calculator routes. API `url_path` may be a full URL,
 * a path without the calculator prefix, or only a report id — otherwise navigation falls through to
 * `<Redirect to="/" />` and the app lands on home.
 */
const calculatorReportNavPath = (urlPath: unknown, fallbackReportId?: string | null): string | null => {
  let raw = urlPath != null && String(urlPath).trim() ? String(urlPath).trim() : '';
  if (!raw && fallbackReportId != null && String(fallbackReportId).trim()) {
    raw = String(fallbackReportId).trim();
  }
  if (!raw) return null;
  try {
    if (/^https?:\/\//i.test(raw)) {
      raw = new URL(raw).pathname;
    }
  } catch {
    /* malformed absolute URL — continue with raw string */
  }
  raw = raw.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!raw) return null;
  if (!raw.startsWith(CALCULATOR_PATH_PREFIX)) {
    raw = `${CALCULATOR_PATH_PREFIX}/${raw}`;
  }
  return `/${raw}`;
};

const EstimatorPage = ({
  setSubscriptionModalOpen,
  user,
  isLoggedIn,
  loginModalOpen,
  setLoginModalOpen,
  matches,
  currency,
  setIsEmailVerifyModalOpen,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  dummyMarket,
  loginCountryCode,
  setEstimatorSimplePricingModalViewMode,
  dummyOverviewMarket,
  setShowTrial,
  handleOnSampleClick,
  resetCurrencyToDefault,
  OnBoardingSearchBox,
  searchbarOnly,
  ota = 'airbnb',
}: EstimatorDashboardPropTypes) => {
  const history = useHistory();
  const estimatorHistoryState = window.history.state;

  const pathname = window.location.pathname;
  const calculatorBase = 'airbnb-calculator';
  const pathSegments = pathname.split('/').filter(Boolean);
  const baseIndex = pathSegments.indexOf(calculatorBase);
  const reportId =
    baseIndex >= 0 && pathSegments.length > baseIndex + 1
      ? pathSegments[pathSegments.length - 1]
      : '';
  const isReportPage = reportId !== '';
  const hasIncomingAddressRef = useRef(!!estimatorHistoryState?.address_full);
  const hasIncomingAddress = hasIncomingAddressRef.current;

  const dispatch = useDispatch();

  const [savedReports, setSavedReports] = useState<EstimatorSavedReportsState>({
    data: null,
    tag_summary: [],
    untagged_count: 0,
    loading: false,
  });
  const [reportData, setReportData] = useState<any>(null);
  const [isReportLoading, setIsReportLoading] = useState(hasIncomingAddress && !isReportPage);
  const loadingStartRef = useRef<number>(0);
  const calculatorReadAbortRef = useRef<AbortController | null>(null);
  const [reportErrorMessage, setReportErrorMessage] = useState<string | null>(null);
  const [reportUpdated, setReportUpdated] = useState(false);
  const [currencyHistory, setCurrencyHistory] = useState<string[]>([]);
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);

  const [selectedKpiPercentile, setSelectedKpiPercentile] = useState<KpiPercentileType>('50');
  const [isApiRateLimited, setIsApiRateLimited] = useState<boolean>(false);

  useEffect(() => {
    resetCurrencyToDefault();
    if (!reportId && reportData) {
      setReportData(_ => null);
    }
  }, [reportId]);

  useEffect(() => {
    if (isLoggedIn && !user.isVerified) {
      setIsEmailVerifyModalOpen(true);
    }
  }, [user, isLoggedIn]);

  const { triggerPaymentInterest } = useSetUserPaymentInterest();

  const handleLoginOrPricingModalOpen = (
    state: 'open' | 'close',
    forceOpen: 'payment' | 'emailVerify' | null = null,
    estimatorSimplePricingModalViewMode: 'expert' | 'scale' = 'expert',
    triggerPaymentInterestAllowed: boolean = true
  ) => {
    setEstimatorSimplePricingModalViewMode(estimatorSimplePricingModalViewMode);

    if (user.email === 'Trial') {
      if (!hasIncomingAddress) {
        setLoginModalOpen(state === 'open' ? true : false);
      }
      return;
    }

    if (forceOpen === 'payment') {
      triggerPaymentInterestAllowed && triggerPaymentInterest();
      setSubscriptionModalOpen(true);
      return;
    }

    if (forceOpen === 'emailVerify') {
      setIsEmailVerifyModalOpen(true);
      return;
    }

    if (user.estimatorCredits > 0 || user.subscriptionPlan !== 'starter') {
      return;
    }

    setSubscriptionModalOpen(state === 'open' ? true : false);
    triggerPaymentInterestAllowed && state === 'open' && triggerPaymentInterest();

    return;
  };

  const handleCreateNewReport = () => {
    if (user.email === 'Trial') {
      setLoginModalOpen(true);
      return;
    }
    setReportData(null);
    history.push('/airbnb-calculator');
  };

  const convertReportDataCurrency = (reportData: any, currency: string, prevCurrency: string) => {
    let convertedApiObject: any = null;
    if (prevCurrency === 'USD') {
      convertedApiObject = convertApiCurrency(deepCopyObject(reportData), currency);
    } else {
      const convertedApiObjectToUSD = convertApiCurrency(
        deepCopyObject(reportData),
        prevCurrency,
        true
      );
      convertedApiObject = convertApiCurrency(convertedApiObjectToUSD, currency);
    }
    return convertedApiObject;
  };

  const convertReportBodyIntoUSD = (reportData: any, currency: string) => {
    const convertedApiObject = convertApiCurrency(deepCopyObject(reportData), currency, true, true);
    return convertedApiObject;
  };

  const checkApiRateLimit = (response: any) => {
    if (response && response.status == 429) {
      setIsApiRateLimited(true);
    }
  };

  const MINIMUM_CREATE_LOADER_MS = 800;

  const handleReport = async (
    method,
    requestBody: null | any,
    id?: string,
    storeID = false,
    hideLoading = false,
    forcePercentile?: KpiPercentileType,
    readSignal?: AbortSignal
  ): Promise<{ success: boolean }> => {
    let readAbortController: AbortController | null = null;
    !hideLoading && setIsReportLoading(true);
    if (method === 'CREATE') loadingStartRef.current = Date.now();
    setReportErrorMessage(null);
    setReportUpdated(false);
    method === 'DELETE' &&
      setSavedReports(prev => ({
        ...prev,
        loading: false,
      }));

    const url = CALCULATOR_API_URL;
    let data;
    setIsApiRateLimited(false);

    try {
      switch (method) {
        case 'CREATE':
          requestBody = convertReportBodyIntoUSD(requestBody, currency);
          data = await callApi(
            url,
            { body: { ...requestBody, ota }, queryParams: { apiType: 'v3' } },
            true,
            false,
            '',
            'POST',
            true,
            undefined,
            true,
            true
          );
          checkApiRateLimit(data);
          break;
        case 'READ':
          calculatorReadAbortRef.current?.abort();
          readAbortController = new AbortController();
          calculatorReadAbortRef.current = readAbortController;
          if (readSignal) {
            if (readSignal.aborted) readAbortController.abort();
            else
              readSignal.addEventListener('abort', () => readAbortController!.abort(), {
                once: true,
              });
          }
          data = await callApi(
            url,
            { queryParams: { id, apiType: 'v3' } },
            true,
            false,
            '',
            'GET',
            true,
            readAbortController.signal,
            true,
            true
          );
          checkApiRateLimit(data);
          break;
        case 'UPDATE':
          requestBody = convertReportBodyIntoUSD(requestBody, currency);
          data = await callApi(
            url,
            { body: { ...requestBody, ota }, queryParams: { apiType: 'v3' } },
            true,
            false,
            '',
            'PUT',
            true,
            undefined,
            true,
            true
          );
          checkApiRateLimit(data);
          break;
        case 'DELETE':
          data = await callApi(
            url,
            { queryParams: { id, apiType: 'v3' } },
            true,
            false,
            '',
            'DELETE',
            true,
            undefined,
            true,
            true
          );
          checkApiRateLimit(data);
          break;
        default:
          console.error(`Unknown method: ${method}`);
          setIsReportLoading(false);
          return { success: false };
      }

      const isCurrencyNeedsToBeConverted = currency !== 'USD';

      if (data.error) {
        setReportErrorMessage(data.error);
        method === 'DELETE' &&
      setSavedReports(prev => ({
        ...prev,
        loading: false,
      }));
        return { success: false };
      }

      if (method === 'DELETE') {
        setSavedReports({
          data: data?.reports,
          tag_summary: data?.tag_summary ?? [],
          untagged_count: data?.untagged_count ?? 0,
          loading: false,
        });
        if (forcePercentile) {
          setSelectedKpiPercentile(forcePercentile);
        }
        return { success: true };
      }

      const url_path = data?.primary_input?.url_path;
      const reportNavPath = calculatorReportNavPath(url_path, data?.primary_input?.id);

      if (hideLoading) {
        // Silent updates (polling READ, KPI persistence, property-note autosave): merge
        // primary_input when comps are unchanged and the response has no comps list yet,
        // so filter/calculator PUTs do not wipe comps during async v3 flows.
        setReportData(prevData => {
          let newData: any;
          try {
            newData = isCurrencyNeedsToBeConverted
              ? convertApiCurrency(data, currency)
              : data;
          } catch (e) {
            console.error('handleReport: currency convert failed', e);
            return prevData;
          }
          if (!newData?.primary_input) {
            return prevData;
          }
          if (!prevData) {
            return newData;
          }
          const compsStatusChanged =
            prevData.primary_input?.comps_status !== newData.primary_input?.comps_status;
          const newCompsHasListings =
            Array.isArray(newData.comps) && newData.comps.length > 0;
          if (compsStatusChanged || newCompsHasListings) {
            return newData;
          }
          const piSame = JSON.stringify(newData.primary_input) === JSON.stringify(prevData.primary_input);
          const fiSame = JSON.stringify(newData.fincal_input ?? null) === JSON.stringify(prevData.fincal_input ?? null);
          const kpiSame = JSON.stringify(newData.kpi ?? null) === JSON.stringify(prevData.kpi ?? null);
          if (piSame && fiSame && kpiSame) {
            return prevData;
          }
          return {
            ...prevData,
            ...newData,
            comps: prevData.comps,
            primary_input: {
              ...prevData.primary_input,
              ...newData.primary_input,
            },
            fincal_input: newData.fincal_input ?? prevData.fincal_input,
            kpi: newData.kpi ?? prevData.kpi,
          };
        });
      } else {
        try {
          const newData = isCurrencyNeedsToBeConverted
            ? convertApiCurrency(data, currency)
            : data;
          if (!newData?.primary_input) {
            setReportErrorMessage('Invalid report response from server');
            if (forcePercentile) {
              setSelectedKpiPercentile(forcePercentile);
            }
            return { success: false };
          }
          setReportData(newData);
        } catch (e) {
          console.error('handleReport: failed to apply report data', e);
          setReportErrorMessage('Failed to update report data');
          if (forcePercentile) {
            setSelectedKpiPercentile(forcePercentile);
          }
          return { success: false };
        }
      }

      storeID &&
        data?.primary_input?.id &&
        localStorage.setItem('estimator-unmapped-report', data.primary_input.id);

      if (method === 'CREATE' && reportNavPath) {
        history.replace(reportNavPath);
      }
      method === 'CREATE' && handleCreditDeduction(user, dispatch);

      if (method === 'READ' && !hideLoading && reportNavPath) {
        if (history.location.pathname !== reportNavPath) {
          history.push(reportNavPath);
        }
      }

      if (forcePercentile) {
        setSelectedKpiPercentile(forcePercentile);
      }
      return { success: true };
    } catch (error) {
      const aborted =
        (error instanceof DOMException && error.name === 'AbortError') ||
        (error instanceof Error && error.name === 'AbortError');
      if (aborted) {
        return { success: false };
      }
      console.error('Error handling report:', error);
      return { success: false };
    } finally {
      if (
        method === 'READ' &&
        readAbortController &&
        calculatorReadAbortRef.current === readAbortController
      ) {
        calculatorReadAbortRef.current = null;
      }
      if (method === 'CREATE') {
        const elapsed = Date.now() - loadingStartRef.current;
        const remaining = MINIMUM_CREATE_LOADER_MS - elapsed;
        if (remaining > 0) {
          setTimeout(() => setIsReportLoading(false), remaining);
        } else {
          setIsReportLoading(false);
        }
      } else {
        setIsReportLoading(false);
      }
    }
  };

  useEffect(() => {
    if (user.isVerified) {
      const unmappedReportID = localStorage.getItem('estimator-unmapped-report');
      if (!unmappedReportID) {
        return;
      }

      const authenticateReportObj = {
        update_type: 'authenticate_report',
        id: unmappedReportID,
      };
      handleReport('UPDATE', authenticateReportObj);

      localStorage.removeItem('estimator-unmapped-report');
    }
  }, [user.isVerified]);

  // Mount-only READ: skip when CREATE already populated reportData for this URL (refresh/deep-link still have null reportData).
  useEffect(() => {
    if (!isReportPage || !reportId || searchbarOnly) return;
    if (reportData?.primary_input?.id === reportId) return;
    handleReport('READ', null, reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUpdatedCurrencyHistory = () => {
    const newCurrencyHistory = [...currencyHistory];
    newCurrencyHistory.push(currency);
    newCurrencyHistory.length > 2 && newCurrencyHistory.shift();
    return newCurrencyHistory;
  };

  //Maintains the currency history so I will know which currency it was previously
  useEffect(() => {
    const updatedCurrencyHistory = getUpdatedCurrencyHistory();
    setCurrencyHistory(_ => updatedCurrencyHistory);
  }, [currency]);

  useEffect(() => {
    if (!reportData) return;

    const prevCurrency = currencyHistory[0];
    const convertedApiObject: any = convertReportDataCurrency(reportData, currency, prevCurrency);

    setReportData(_ => convertedApiObject);
  }, [currencyHistory]);

  useEffect(() => {
    const initializeReportCreation = async () => {
      if (!estimatorHistoryState?.address_full) return;

      const createReportBody = await getCreateReportBodyFromHistoryState(estimatorHistoryState);
      if (!createReportBody) return;

      const storeID = user.email === 'Trial';
      handleReport('CREATE', createReportBody, undefined, storeID);
    };
    initializeReportCreation();
  }, []);

  useEffect(() => {
    if (!hasIncomingAddress || isReportPage) return;
    const timeout = setTimeout(() => {
      if (!reportData) setIsReportLoading(false);
    }, 15000);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      {!searchbarOnly && (
        <Helmet>
          <meta charSet="utf-8" />
          <title>
            {ota === 'airbnb'
              ? 'Airbnb Profit Calculator: How much can I make on Airbnb?'
              : 'Booking.com Profit Calculator: How much can I make on Booking.com?'}
          </title>
          <link rel="canonical" href="https://airbtics.com/airbnb-income-calculator/" />
        </Helmet>
      )}

      {!reportData &&
        !isReportLoading &&
        !(isReportPage && reportId && !searchbarOnly) && (
        <SearchPage
          user={user}
          matches={matches}
          currency={currency}
          dummyMarket={dummyMarket}
          googleAutocompleteSessionToken={googleAutocompleteSessionToken}
          getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
          clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
          googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
          handleReport={handleReport}
          handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
          setComingSoonModalOpen={setComingSoonModalOpen}
          savedReports={savedReports}
          setSavedReports={setSavedReports}
          resetCurrencyToDefault={resetCurrencyToDefault}
          searchbarOnly={searchbarOnly}
          ota={ota}
          isApiRateLimited={isApiRateLimited}
          setIsApiRateLimited={setIsApiRateLimited}
        />
      )}

      {!searchbarOnly &&
        isReportPage &&
        reportId &&
        !reportData &&
        !isApiRateLimited && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '45vh',
            }}
          >
            <CircularProgress />
          </Box>
        )}

      {isApiRateLimited ? (
        <Grid container item xs={12}>
          <RateLimitAlert />
        </Grid>
      ) : (
        reportData && (
          <ReportPage
            key={reportData.primary_input.id}
            reportData={reportData}
            currency={currency}
            handleCreateNewReport={handleCreateNewReport}
            handleReport={handleReport}
            matches={matches}
            user={user}
            handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
            setComingSoonModalOpen={setComingSoonModalOpen}
            dummyOverviewMarket={dummyOverviewMarket}
            loginCountryCode={loginCountryCode}
            handleOnSampleClick={handleOnSampleClick}
            ota={ota}
            selectedKpiPercentile={selectedKpiPercentile}
            setSelectedKpiPercentile={setSelectedKpiPercentile}
            isApiRateLimited={isApiRateLimited}
          />
        )
      )}

      {!searchbarOnly && isReportLoading && !loginModalOpen && <FullScreenAnimationLoader />}

      {!searchbarOnly && reportErrorMessage && (
        <AutohideSnackbar message={reportErrorMessage} type="error" />
      )}

      {comingSoonModalOpen && (
        <ComingSoonModal open={comingSoonModalOpen} setOpen={setComingSoonModalOpen} />
      )}
    </>
  );
};

export default EstimatorPage;
